/**
 * Phone number normalization. Extracted from server.mjs – behavior preserved.
 * Normalizes to E.164 format (+1xxxxxxxxxx for US).
 *
 * **Telephony / Join via Phone (canonical DB field):** `users.phone_e164`
 * (`handleAgentJoinPhone` may read legacy `phone_number` only for older rows).
 * On write, set both `phone_e164` and `phone_number` to the same E.164 value.
 *
 * **Profile policy:** Roles in `ROLES_REQUIRING_PROFILE_PHONE` must keep a valid E.164 (login / MFA parity).
 */
export const ROLES_REQUIRING_PROFILE_PHONE = ['agent', 'tenant_admin'];

function stripPhoneExtension(input) {
  const text = String(input).trim();
  const match = text.match(/^(.*?)(?:\s*(?:ext\.?|extension|x|#)\s*\d+)\s*$/i);
  if (!match) return text;
  const head = match[1].trim();
  if (head.replace(/\D/g, '').length < 10) return text;
  return head;
}

export function normalizePhoneNumber(input) {
  if (!input) return null;
  let raw = stripPhoneExtension(input);
  if (!raw) return null;
  // Drop a national trunk prefix written as +CC (0) so the result stays E.164.
  raw = raw.replace(/^(\+\d{1,3})\s*\(0\)\s*/, '$1');
  let p = raw.replace(/[\s().-]/g, '');
  if (!p) return null;
  if (p.startsWith('00')) {
    const rest = p.slice(2);
    if (/^[1-9]\d{9,14}$/.test(rest)) p = `+${rest}`;
  }
  if (p[0] !== '+') {
    if (/^\d{11}$/.test(p) && p[0] === '1') p = `+${p}`;
    else if (/^\d{10}$/.test(p)) p = `+1${p}`;
    else p = `+1${p}`;
  }
  return p;
}

/** Alias for API/docs; same as normalizePhoneNumber. */
export function normalizeToE164(input) {
  return normalizePhoneNumber(input);
}

/** Validate E.164 format: + followed by 10–15 digits. */
export function validateE164(input) {
  const n = normalizePhoneNumber(input);
  if (!n) return false;
  return /^\+[1-9]\d{9,14}$/.test(n);
}
